import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '@/components/ui/page-header';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Users, MoreVertical } from 'lucide-react';
import { usePermissions } from '@/common/hooks/usePermissions';
import { rolesService, Role } from '@/features/roles/services/roles.service';
import { RoleFormDialog } from '@/features/roles/components/RoleFormDialog';
import { RoleUsersDialog } from '@/features/roles/components/RoleUsersDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export const Route = createFileRoute('/_authenticated/roles')({
  component: RolesPage,
});

function RolesPage() {
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [formOpen, setFormOpen] = useState(false);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  const canManage = hasPermission('roles.manage');

  // Buscar roles
  const { data: rolesData, isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const response = await rolesService.getAll();
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: canManage,
  });

  const roles: Role[] = rolesData || [];

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: (id: string) => rolesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });

  const handleCreate = () => {
    setSelectedRole(null);
    setFormOpen(true);
  };

  const handleEdit = (role: Role) => {
    setSelectedRole(role);
    setFormOpen(true);
  };

  const handleViewUsers = (role: Role) => {
    setSelectedRole(role);
    setUsersDialogOpen(true);
  };

  const handleDelete = async (role: Role) => {
    const ok = await confirm({
      title: `Excluir o cargo "${role.name}"?`,
      description: 'Esta ação não pode ser desfeita.',
      confirmLabel: 'Excluir cargo',
      destructive: true,
    });
    if (!ok) return;

    try {
      await deleteMutation.mutateAsync(role.id);
    } catch (error) {
      // o interceptor global já mostra o toast de erro
      console.error('Erro ao excluir role:', error);
    }
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['roles'] });
    setFormOpen(false);
  };

  if (!canManage) {
    return (
      <div className="space-y-6">
        <PageHeader title="Cargos e permissões" description="Você não tem permissão para acessar esta página" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cargos e permissões"
        description="Gerencie cargos e suas permissões"
        actions={
          <Button onClick={handleCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            Novo cargo
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Lista de cargos</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">Carregando...</div>
          ) : !roles || roles.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">Nenhum cargo encontrado</div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Permissões</TableHead>
                    <TableHead>Usuários</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roles.map((role) => (
                    <TableRow key={role.id}>
                      <TableCell className="font-medium">{role.name}</TableCell>
                      <TableCell>
                        {role.description ? (
                          <span className="text-sm text-muted-foreground">{role.description}</span>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {role.permissionCount || role.permissions?.length || 0} permissões
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className="cursor-pointer text-xs"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewUsers(role);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleViewUsers(role);
                            }
                          }}
                        >
                          {role.userCount || 0} usuários
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label={`Ações do cargo ${role.name}`}>
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onSelect={(e) => {
                                e.preventDefault();
                                handleEdit(role);
                              }}
                            >
                              <Edit className="mr-2 h-4 w-4" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onSelect={(e) => {
                                e.preventDefault();
                                handleViewUsers(role);
                              }}
                            >
                              <Users className="mr-2 h-4 w-4" />
                              Ver Usuários
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onSelect={() => handleDelete(role)}
                              className="text-destructive"
                            >
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modais */}
      <RoleFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        onSuccess={handleSuccess}
        role={selectedRole || undefined}
      />

      <RoleUsersDialog
        open={usersDialogOpen}
        onOpenChange={setUsersDialogOpen}
        role={selectedRole}
      />
    </div>
  );
}

